/**
 * Générateur — Connaître les critères de divisibilité par 3 et par 9.
 * Attendu ID : 700 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 6)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « cocher » — Ex.11 Divisibilité par 3 et 9
 *     Formats : qcm_multiple (compte annoncé, rotation par 3 / par 9),
 *     tableau_vf. Nombres CONSTRUITS PAR LEURS CHIFFRES (somme contrôlée
 *     ≡ 0 ou ≢ 0 mod 3/9) et GRANDS (4-5 chiffres) : le critère est le seul
 *     chemin. Le « tableau à cocher 3/9 » du doc devient V/F (une coche par
 *     ligne dans l'app ; divisible par 9 ⇒ par 3 rendait les colonnes non
 *     exclusives) — arbitrage validé.
 *
 *   Carte « croises » — Ex.6 Divisibles à la fois par…
 *     Formats : tableau colonnesLibres à CATÉGORIES EXCLUSIVES (« Par p
 *     seulement / Par q seulement / Par p et par q / Ni l'un ni l'autre » —
 *     une coche par ligne, richesse supérieure au tableau du doc : l'élève
 *     teste LES DEUX critères pour chaque nombre), qcm_multiple (rotation
 *     3&4 / 3&7 / 4&7 / 3&4&7, compte annoncé). Nombres bâtis exprès
 *     (multiples du ppcm = les « oui », multiples d'un seul = les pièges).
 *
 *   Carte « completer » — Ex.12 Complète avec un chiffre (ADAPTÉ, validé)
 *     Formats : texte (saisie d'un chiffre), qcm_unique. Le doc note que
 *     « plusieurs chiffres valides » doivent être acceptés — impossible au
 *     scoring exact. Adaptation : « le PLUS PETIT chiffre possible » (saisie,
 *     unicité par consigne) ; en QCM, base construite pour qu'UN SEUL des 4
 *     chiffres proposés convienne. Critères 3, 9, 6 (combinaison) en saisie ;
 *     2, 3, 9 en QCM.
 *
 *   Carte « tartelettes » — Ex.18 Les tartelettes du pâtissier
 *     Format : tableau_vf. Quantités bâties par RÉGIMES de divisibilité
 *     (multiples de 15 / de 10 / de 9 / de 45 avec cofacteurs contraints) →
 *     les verdicts pour {3, 5, 9, 10} varient d'un tirage à l'autre (pas de
 *     pattern mémorisable).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 700;

const L_COCHER = 'Divisible par 3 ? par 9 ?';
const L_CROISE = 'Divisibles à la fois par…';
const L_COMPL  = 'Complète avec un chiffre';
const L_TARTE  = 'Les tartelettes du pâtissier';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOTS_COMPTE = { 2: 'deux', 3: 'trois', 4: 'quatre' };

/** Somme des chiffres. */
const sommeChiffres = (n) => String(n).split('').reduce((s, c) => s + parseInt(c, 10), 0);

/**
 * Construit un nombre de `nb` chiffres dont la somme des chiffres est ≡ cible
 * (mod m) — ou ≢ si `divisible` est faux. Le dernier chiffre est AJUSTÉ.
 */
const construireNombre = (nbChiffres, m, divisible) => {
    const chiffres = [randInt(1, 9)];
    for (let i = 1; i < nbChiffres - 1; i++) chiffres.push(randInt(0, 9));
    const s = chiffres.reduce((x, y) => x + y, 0);
    let dernier = (m - (s % m)) % m;                    // rend la somme ≡ 0 (mod m)
    if (!divisible) {
        dernier = (dernier + randInt(1, m - 1)) % 10;   // décale hors du critère
        if ((s + dernier) % m === 0) dernier = (dernier + 1) % 10;
    } else if (dernier + m <= 9 && randomChoice([true, false])) {
        dernier += m;                                    // variante (somme ≡ 0 aussi)
    }
    return parseInt(chiffres.join('') + dernier, 10);
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Divisible par 3 ? par 9 ?
// ═══════════════════════════════════════════════════════════

const genCocherQcmMulti = () => {
    const m = randomChoice([3, 9]);
    const nbBons = randInt(2, 4);
    const nombres = [];
    for (let i = 0; i < 6; i++) {
        nombres.push({
            n: construireNombre(randInt(4, 5), m, i < nbBons),
            bon: i < nbBons,
        });
    }
    // Unicité des affichages (collision improbable mais bornée)
    const vus = new Set();
    nombres.forEach(o => {
        while (vus.has(o.n)) o.n += m * 10;             // conserve la divisibilité mod m
        vus.add(o.n);
    });
    const pool = shuffle(nombres);
    const corrects = pool.filter(o => o.bon).map(o => String(o.n));
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[nbBons]}</strong> nombres divisibles ` +
        `par <strong>${m}</strong>. <em>(Utilise le critère : la somme des chiffres.)</em></p>`;
    return makeQcm(pool.map(o => String(o.n)), corrects, texte, opts(L_COCHER), true);
};

const genCocherVF = () => {
    let lignes;
    for (let i = 0; i < 30; i++) {
        lignes = [0, 1, 2, 3].map(() => {
            const m = randomChoice([3, 9]);
            const divisible = randomChoice([true, false]);
            return {
                txt: `${construireNombre(randInt(4, 5), m, divisible)} est divisible par ${m}`,
                v: divisible,
            };
        });
        const nbVrais = lignes.filter(l => l.v).length;
        if (nbVrais >= 1 && nbVrais <= 3 && new Set(lignes.map(l => l.txt)).size === 4) break;
    }
    const texte = '<p>À l\'aide des critères de divisibilité, indique si chaque phrase ' +
        'est vraie ou fausse.</p>';
    return makeVraiFaux(lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_COCHER));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Divisibles à la fois par…
// ═══════════════════════════════════════════════════════════

const COUPLES_CROISES = [[3, 4], [3, 7], [4, 7]];

/** Multiplie k jusqu'à éviter la divisibilité par `interdit`. */
const multipleDe = (base, interdit) => {
    for (let i = 0; i < 30; i++) {
        const n = base * randInt(2, 25);
        if (n % interdit !== 0) return n;
    }
    return base;                                        // base elle-même (première ligne du tirage)
};

const genCroisesTableau = () => {
    const [p, q] = randomChoice(COUPLES_CROISES);
    const ppcm = p * q;                                  // couples premiers entre eux
    const colonnes = [
        `Divisible par ${p} seulement`,
        `Divisible par ${q} seulement`,
        `Divisible par ${p} et par ${q}`,
        `Ni par ${p} ni par ${q}`,
    ];
    // Un nombre par catégorie + 2 supplémentaires → 6 lignes, une coche chacune
    const tirages = [
        { n: multipleDe(p, q), col: 0 },
        { n: multipleDe(q, p), col: 1 },
        { n: ppcm * randInt(1, 12), col: 2 },
        { n: (() => { let x; do { x = randInt(11, 299); } while (x % p === 0 || x % q === 0); return x; })(), col: 3 },
        { n: multipleDe(p, q), col: 0 },
        { n: ppcm * randInt(13, 25), col: 2 },
    ];
    // Lignes distinctes — retouche bornée qui PRÉSERVE la catégorie de chaque nombre
    const estDansCategorie = (n, col) => {
        if (col === 0) return n % p === 0 && n % q !== 0;
        if (col === 1) return n % q === 0 && n % p !== 0;
        if (col === 2) return n % ppcm === 0;
        return n % p !== 0 && n % q !== 0;
    };
    const vus = new Set();
    tirages.forEach(t => {
        for (let i = 0; (vus.has(t.n) || !estDansCategorie(t.n, t.col)) && i < 60; i++) {
            t.n += (t.col === 2 ? ppcm : t.col === 0 ? p : t.col === 1 ? q : 1);
        }
        vus.add(t.n);
    });
    const ordre = shuffle(tirages);
    const lignes = ordre.map(t => String(t.n));
    const bonnesReponses = ordre.map(t => t.col);
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const texte = '<p>Pour chaque nombre, coche la colonne qui convient. ' +
        `<em>(Teste les deux critères : par ${p} et par ${q}.)</em></p>`;
    return {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'table', config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses } }],
        },
        niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID,
        _meta: { label: L_CROISE },
    };
};

const genCroisesQcmMulti = () => {
    const triple = randomChoice([false, false, true]);
    const facteurs = triple ? [3, 4, 7] : randomChoice(COUPLES_CROISES);
    const ppcm = facteurs.reduce((x, y) => x * y, 1);    // 3, 4, 7 premiers entre eux
    const nbBons = randInt(2, 3);
    const bons = [];
    const vus = new Set();
    for (let i = 0; i < nbBons; i++) {
        let n = ppcm * randInt(1, triple ? 8 : 20);
        while (vus.has(n)) n += ppcm;
        vus.add(n);
        bons.push(n);
    }
    const pieges = [];
    while (pieges.length < 6 - nbBons) {
        const f = randomChoice(facteurs);
        const n = multipleDe(f, facteurs.find(x => x !== f));
        if (!vus.has(n) && n % ppcm !== 0) { vus.add(n); pieges.push(n); }
    }
    const choix = shuffle([...bons, ...pieges]).map(String);
    const liste = facteurs.length === 3
        ? `${facteurs[0]}, ${facteurs[1]} et ${facteurs[2]}`
        : `${facteurs[0]} et ${facteurs[1]}`;
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[nbBons]}</strong> nombres divisibles ` +
        `<strong>à la fois</strong> par ${liste}.</p>`;
    return makeQcm(choix, bons.map(String), texte, opts(L_CROISE), true);
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Complète avec un chiffre
// ═══════════════════════════════════════════════════════════

/** Nombre à trou : chiffres connus + position du trou. Affichage espacé. */
const tirerBaseTrou = (nbChiffres, trouAuBout) => {
    const chiffres = [randInt(1, 9)];
    for (let i = 1; i < nbChiffres; i++) chiffres.push(randInt(0, 9));
    const pos = trouAuBout ? nbChiffres - 1 : randInt(1, nbChiffres - 1);
    return { chiffres, pos, somme: chiffres.reduce((a, b, i) => (i === pos ? a : a + b), 0) };
};

const afficherAvecTrou = (chiffres, pos) =>
    chiffres.map((c, i) => (i === pos ? '◻' : String(c))).join(' ');

const genCompleterSaisie = () => {
    const crit = randomChoice([3, 9, 6]);
    const { chiffres, pos, somme } = tirerBaseTrou(randInt(3, 4), crit === 6);
    let plusPetit;
    if (crit === 6) {
        // pair ET somme divisible par 3 → plus petit chiffre pair qui convient
        plusPetit = [0, 2, 4, 6, 8].find(x => (somme + x) % 3 === 0);
    } else {
        plusPetit = (crit - (somme % crit)) % crit;
    }
    const texte = `<p>Quel est le <strong>plus petit chiffre</strong> à écrire à la place de ◻ ` +
        `pour que le nombre <strong>${afficherAvecTrou(chiffres, pos)}</strong> soit divisible ` +
        `par <strong>${crit}</strong> ?</p>`;
    return makeSaisie(String(plusPetit), texte, opts(L_COMPL));
};

const genCompleterQcm = () => {
    const crit = randomChoice([2, 3, 9]);
    const { chiffres, pos, somme } = tirerBaseTrou(randInt(3, 4), crit === 2);
    let valide;
    let faux;
    if (crit === 2) {
        valide = randomChoice([0, 2, 4, 6, 8]);
        faux = shuffle([1, 3, 5, 7, 9]).slice(0, 3);
    } else if (crit === 3) {
        const x0 = (3 - (somme % 3)) % 3;
        valide = randomChoice([x0, x0 + 3, x0 + 6].filter(x => x <= 9));
        faux = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(x => (somme + x) % 3 !== 0)).slice(0, 3);
    } else {
        valide = (9 - (somme % 9)) % 9;
        faux = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(x => (somme + x) % 9 !== 0)).slice(0, 3);
    }
    const texte = `<p>Quel chiffre peut-on écrire à la place de ◻ pour que le nombre ` +
        `<strong>${afficherAvecTrou(chiffres, pos)}</strong> soit divisible par <strong>${crit}</strong> ?</p>`;
    return makeQcm([String(valide), ...faux.map(String)], String(valide), texte, opts(L_COMPL));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Les tartelettes du pâtissier
// ═══════════════════════════════════════════════════════════

const CTX_TARTES = [
    { qui: 'Un pâtissier', f1: 'morceaux de pommes', f2: 'framboises', quoi: 'tartelettes' },
    { qui: 'Une cuisinière', f1: 'tomates cerises', f2: 'olives', quoi: 'brochettes' },
    { qui: 'Un fleuriste', f1: 'roses', f2: 'marguerites', quoi: 'compositions' },
];

/** Régimes de divisibilité — les verdicts pour {3, 5, 9, 10} varient. */
const REGIMES_TARTES = [
    { fac: 15, contrainte: (k) => k % 3 !== 0 && k % 2 !== 0 },   // 3✓ 5✓ 9✗ 10✗
    { fac: 10, contrainte: (k) => k % 3 !== 0 },                  // 3✗ 5✓ 9✗ 10✓
    { fac: 9,  contrainte: (k) => k % 5 !== 0 && k % 2 !== 0 },   // 3✓ 5✗ 9✓ 10✗
    { fac: 45, contrainte: (k) => k % 2 !== 0 },                  // 3✓ 5✓ 9✓ 10✗
];

const tirerTartes = () => {
    const regime = randomChoice(REGIMES_TARTES);
    const tirerK = () => {
        for (let i = 0; i < 40; i++) {
            const k = randInt(3, 25);
            if (regime.contrainte(k)) return k;
        }
        return 7;                                        // repli : impair, non multiple de 3 ni 5
    };
    const ka = tirerK();
    let kb = tirerK();
    if (kb === ka) kb = tirerK();
    return { a: regime.fac * ka, b: regime.fac * kb };
};

const genTartesVF = () => {
    const { a, b } = tirerTartes();
    const ctx = randomChoice(CTX_TARTES);
    const candidats = shuffle([3, 5, 9, 10]);
    const lignesTxt = candidats.map(n => `Préparer ${n} ${ctx.quoi} identiques est possible`);
    const bonnes = candidats.map(n => (a % n === 0 && b % n === 0 ? 0 : 1));
    const texte = `<p>${ctx.qui} dispose de <strong>${a} ${ctx.f1}</strong> et de ` +
        `<strong>${b} ${ctx.f2}</strong>, et veut préparer des ${ctx.quoi} ` +
        '<strong>toutes identiques</strong> en utilisant tous les fruits.</p>' +
        '<p>À l\'aide des critères de divisibilité, indique si chaque affirmation est vraie ou fausse.</p>';
    return makeVraiFaux(lignesTxt, bonnes, texte, opts(L_TARTE));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    cocher_qcm_multiple:    genCocherQcmMulti,
    cocher_tableau_vf:      genCocherVF,
    croises_tableau:        genCroisesTableau,
    croises_qcm_multiple:   genCroisesQcmMulti,
    completer_texte:        genCompleterSaisie,
    completer_qcm_unique:   genCompleterQcm,
    tartelettes_tableau_vf: genTartesVF,
};

export const CATEGORIES = [
    {
        id: 'cocher', label: L_COCHER,
        formatsList: [
            { key: 'cocher_qcm_multiple', label: 'QCM Multi' },
            { key: 'cocher_tableau_vf',   label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'croises', label: L_CROISE,
        formatsList: [
            { key: 'croises_tableau',      label: 'Tableau' },
            { key: 'croises_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'completer', label: L_COMPL,
        formatsList: [
            { key: 'completer_texte',      label: 'Saisie' },
            { key: 'completer_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'tartelettes', label: L_TARTE,
        formatsList: [{ key: 'tartelettes_tableau_vf', label: 'Vrai/Faux' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    sommeChiffres, construireNombre, multipleDe, tirerBaseTrou,
    REGIMES_TARTES, tirerTartes, COUPLES_CROISES,
};
